/**
 * From a direction in the sky to a point on the screen, and back (Sprint 103.0).
 *
 * Pure: no DOM, no clock. The camera is described by three unit vectors in the
 * local East-North-Up frame (`right`, `up`, `forward`), whatever produced them —
 * the device's orientation sensor or a finger dragging the sky. Keeping the pose
 * as a basis rather than as Euler angles is deliberate: the phone is held upright
 * to look at the sun, which is exactly where the sensor's Z-X'-Y'' angles hit
 * their singularity (beta = 90°), and a basis has none.
 */

export type Vector = readonly [number, number, number];

export interface CameraBasis {
  readonly right: Vector;
  readonly up: Vector;
  readonly forward: Vector;
}

export interface Viewport {
  readonly width: number;
  readonly height: number;
  /** Focal length in CSS pixels: how far the image plane sits from the eye. */
  readonly focal: number;
}

export interface ScreenPoint {
  readonly x: number;
  readonly y: number;
  /** In front of the camera; a point behind it has no place on the screen. */
  readonly inFront: boolean;
}

export interface SkyDirection {
  /** Degrees clockwise from true north, in [0, 360). */
  readonly azimuthDegrees: number;
  /** Degrees above the horizon. */
  readonly altitudeDegrees: number;
}

const RADIANS = Math.PI / 180;
const DEGREES = 180 / Math.PI;

export function dot(a: Vector, b: Vector): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

export function cross(a: Vector, b: Vector): Vector {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}

export function normalise(v: Vector): Vector {
  const length = Math.hypot(v[0], v[1], v[2]);
  return length === 0 ? [0, 0, 0] : [v[0] / length, v[1] / length, v[2] / length];
}

/** A sky direction as an East-North-Up unit vector. */
export function toVector(direction: SkyDirection): Vector {
  const az = direction.azimuthDegrees * RADIANS;
  const alt = direction.altitudeDegrees * RADIANS;
  return [Math.cos(alt) * Math.sin(az), Math.cos(alt) * Math.cos(az), Math.sin(alt)];
}

export function toDirection(v: Vector): SkyDirection {
  const [east, north, up] = normalise(v);
  const azimuth = Math.atan2(east, north) * DEGREES;
  return {
    azimuthDegrees: (azimuth + 360) % 360,
    altitudeDegrees: Math.asin(Math.max(-1, Math.min(1, up))) * DEGREES
  };
}

/**
 * Turns a direction about the vertical by `degrees` (clockwise seen from above),
 * which is what a compass correction is: the sensor's north is off by a constant.
 */
export function rotateAzimuth(direction: SkyDirection, degrees: number): SkyDirection {
  return {
    azimuthDegrees: (((direction.azimuthDegrees + degrees) % 360) + 360) % 360,
    altitudeDegrees: direction.altitudeDegrees
  };
}

export function project(
  direction: SkyDirection,
  camera: CameraBasis,
  viewport: Viewport
): ScreenPoint {
  const w = toVector(direction);
  const x = dot(w, camera.right);
  const y = dot(w, camera.up);
  const z = dot(w, camera.forward);
  // Clamp the depth so a point just behind the eye does not land at infinity:
  // callers skip it by `inFront`, but a line drawn to it must stay finite.
  const depth = Math.max(z, 1e-6);
  return {
    x: viewport.width / 2 + (viewport.focal * x) / depth,
    y: viewport.height / 2 - (viewport.focal * y) / depth,
    inFront: z > 0
  };
}

/**
 * The focal length of a camera image shown with `object-fit: cover`.
 *
 * Browsers do not report a camera's field of view, so the long side of the
 * sensor is assumed to span `longSideDegrees` — about 67° for a phone's main
 * (26 mm-equivalent, 4:3) camera. The image is scaled to cover the viewport,
 * so the focal length follows the scaled image, not the viewport: the cropped
 * part is still part of the same lens.
 */
export function coverFocal(
  viewportWidth: number,
  viewportHeight: number,
  imageWidth: number,
  imageHeight: number,
  longSideDegrees: number
): number {
  const scale = Math.max(viewportWidth / imageWidth, viewportHeight / imageHeight);
  const longSide = Math.max(imageWidth, imageHeight) * scale;
  return longSide / 2 / Math.tan((longSideDegrees * RADIANS) / 2);
}
